import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, forceClearSession } from '@/lib/supabase';
import { authContext } from './authContext';
import type { AuthUser, AuthContextValue } from './authContext';

const LOCAL_USER_KEY = 'user';
const LOCAL_CLINIC_KEY = 'clinic_id';

function readLocalUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(LOCAL_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(readLocalUser);
  const [clinicName, setClinicName] = useState('');
  const [clinicLogoUrl, setClinicLogoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Load clinic name + logo once when clinic_id is known
  const loadClinicInfo = useCallback(async (clinicId: string) => {
    try {
      const { data } = await supabase
        .from('clinics')
        .select('name, logo_url')
        .eq('id', clinicId)
        .maybeSingle();
      if (data?.name) setClinicName(data.name);
      if (data?.logo_url) setClinicLogoUrl(data.logo_url);
    } catch { /* ignore */ }
  }, []);

  // Apply user data from any source
  const applyUser = useCallback((u: AuthUser) => {
    setUser((prev) => {
      const merged = prev ? { ...prev, ...u } : u;
      localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(merged));
      return merged;
    });
    if (u.clinic_id) {
      localStorage.setItem(LOCAL_CLINIC_KEY, u.clinic_id);
      loadClinicInfo(u.clinic_id);
    }
  }, [loadClinicInfo]);

  // Refresh user from DB (called once on mount, not on every navigation)
  // FE-SESSION-04: returns true only when remote profile is confirmed.
  // Returns false when session is missing, token is invalid, or profile fetch fails.
  const refreshUser = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) return false;
      const { data } = await supabase.functions.invoke('get-user-profile', {
        headers: { Authorization: `Bearer ${s.access_token}` },
      });
      if (data?.success && data.user) {
        applyUser(data.user as AuthUser);
        return true;
      }
      return false;
    } catch (err) {
      const msg = String(
        (err as Error)?.message || (err as { errorMessage?: string })?.errorMessage || ''
      );
      if (msg.includes('Invalid Refresh Token') || msg.includes('Refresh Token Not Found')) {
        await forceClearSession();
        window.location.reload();
      }
      return false;
    }
  }, [applyUser]);

  // Bootstrap: get session once, then listen for changes
  useEffect(() => {
    let mounted = true;
    let signInRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    let profileRefreshGeneration = 0;

    // O gate FE-SESSION-04 só é liberado com perfil confirmado remotamente.
    const confirmProfile = () => {
      const generation = ++profileRefreshGeneration;
      void refreshUser().then((success) => {
        if (mounted && generation === profileRefreshGeneration) {
          setIsSessionReady(success);
        }
      }).catch(() => {
        if (mounted && generation === profileRefreshGeneration) {
          setIsSessionReady(false);
        }
      });
    };

    const initSession = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(s);
        setIsLoading(false);

        if (s) {
          // FE-SESSION-04: cache local NUNCA libera o gate.
          // O isSessionReady só vira true após refreshUser() confirmar
          // remotamente sessão + perfil + papel + clínica.
          // O cache é usado apenas para preencher a UI temporariamente.
          setIsSessionReady(false);
          confirmProfile();
          const clinicId = localStorage.getItem(LOCAL_CLINIC_KEY);
          if (clinicId) loadClinicInfo(clinicId);
        }
      } catch (err) {
        if (!mounted) return;
        setIsLoading(false);
        const msg = String(
          (err as Error)?.message || (err as { errorMessage?: string })?.errorMessage || ''
        );
        if (msg.includes('Invalid Refresh Token') || msg.includes('Refresh Token Not Found')) {
          await forceClearSession();
          window.location.reload();
        }
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;
      setSession(s);

      if (event === 'SIGNED_OUT') {
        ++profileRefreshGeneration;
        if (signInRefreshTimer !== null) {
          clearTimeout(signInRefreshTimer);
          signInRefreshTimer = null;
        }
        setUser(null);
        setClinicName('');
        setClinicLogoUrl('');
        setIsSessionReady(false);
        localStorage.removeItem(LOCAL_USER_KEY);
        localStorage.removeItem(LOCAL_CLINIC_KEY);
        localStorage.removeItem('auth_user_id');
      }

      if (event === 'SIGNED_IN' && s) {
        // Uma sessão nova também precisa confirmar o perfil remoto.
        // Não chamar APIs Supabase dentro de onAuthStateChange:
        // isso pode bloquear o cliente; executamos fora do callback.
        setIsLoading(false);
        setIsSessionReady(false);
        if (signInRefreshTimer !== null) clearTimeout(signInRefreshTimer);
        signInRefreshTimer = setTimeout(() => {
          signInRefreshTimer = null;
          if (mounted) confirmProfile();
        }, 0);
      }
    });

    // Listen for profile updates dispatched from ProfilePage
    const handleProfileUpdate = (e: Event) => {
      const detail = (e as CustomEvent<AuthUser>).detail;
      if (detail) applyUser(detail);
    };
    window.addEventListener('user_profile_updated', handleProfileUpdate);

    // Listen for clinic updates dispatched from ClinicSettingsPage
    const handleClinicUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string; logo_url?: string }>).detail;
      if (!detail) return;
      if (detail.name) setClinicName(detail.name);
      if (detail.logo_url != null) setClinicLogoUrl(detail.logo_url);
    };
    window.addEventListener('clinic_updated', handleClinicUpdate);

    return () => {
      mounted = false;
      ++profileRefreshGeneration;
      if (signInRefreshTimer !== null) clearTimeout(signInRefreshTimer);
      subscription.unsubscribe();
      window.removeEventListener('user_profile_updated', handleProfileUpdate);
      window.removeEventListener('clinic_updated', handleClinicUpdate);
    };
  }, [refreshUser, applyUser, loadClinicInfo]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      // ignore
    }
    await forceClearSession();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    user,
    clinicName,
    clinicLogoUrl,
    isLoading,
    isAuthenticated: !!session && !!user,
    isSessionReady,
    refreshUser,
    signOut,
  }), [session, user, clinicName, clinicLogoUrl, isLoading, isSessionReady, refreshUser, signOut]);

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
}